import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { useApiMutation } from '@/shared/hooks/useApi';
import { api } from '@/shared/services/api';
import { ApiResponse } from '@/shared/types/apiTypes';
import { validateKRW } from '@/shared/utils/priceUtils';

import { useOrderCreateStore } from '../store/orderCreateStore';
import { CreateOrderRequest, CreateOrderResponse } from '../types/orderTypes';

export const useOrderCreate = () => {
  const navigate = useNavigate();
  const {
    selectedUser,
    getOrderRequestData,
    resetOrderData,
    selectedProducts,
    receiverInfo,
    currencyCode,
  } = useOrderCreateStore();

  const createOrderApi = useCallback(
    async (
      userUuid: string,
      orderData: CreateOrderRequest,
    ): Promise<ApiResponse<CreateOrderResponse>> => {
      return api.post<CreateOrderResponse>(
        `/api/v1/order/${userUuid}`,
        orderData,
      );
    },
    [],
  );

  const createOrderMutation = useApiMutation(
    ({
      userUuid,
      orderData,
    }: {
      userUuid: string;
      orderData: CreateOrderRequest;
    }) => createOrderApi(userUuid, orderData),
    {
      onSuccess: (response) => {
        if (response.success && response.data?.uuid) {
          alert('Order created successfully!');
          navigate(`/orders/${response.data.uuid}`);
          resetOrderData();
        }
      },
      onError: (error) => {
        console.error('Order creation failed:', error);
        alert('Order creation failed. Please try again.');
      },
    },
  );

  const createOrder = useCallback(() => {
    if (!selectedUser) {
      alert('Please select a user.');
      return;
    }

    if (selectedProducts.length === 0) {
      alert('Please select products to order.');
      return;
    }

    if (!receiverInfo.receiverName.trim()) {
      alert('Please enter the recipient name.');
      return;
    }

    if (!receiverInfo.addressDetail.trim()) {
      alert('Please enter the shipping address.');
      return;
    }

    if (!receiverInfo.phoneNumber.trim()) {
      alert('Please enter the phone number.');
      return;
    }

    const orderData = getOrderRequestData();

    if (currencyCode === 'KRW' && !validateKRW(orderData.finalPaymentAmount)) {
      console.warn(
        `KRW amount ${orderData.finalPaymentAmount} was automatically rounded`,
      );
    }

    createOrderMutation.mutate({
      userUuid: selectedUser.uuid,
      orderData,
    });
  }, [
    selectedUser,
    selectedProducts,
    receiverInfo,
    currencyCode,
    getOrderRequestData,
    createOrderMutation,
  ]);

  return {
    createOrder,
    isLoading: createOrderMutation.isPending,
    error: createOrderMutation.error,
  };
};
